import React, { useEffect, useState } from 'react'
import Offcanvas from 'react-bootstrap/Offcanvas'
import { useTranslation } from 'react-i18next'
import LoadingBackdrop from 'components/LoadingBackdrop'
import { extractCountryFromDepartureOffice } from 'shared/ncts/common/classifier-util'
import EventLog from 'features/declaration/event-log/components/EventLog'
import TransitPreviewHeader, { PreviewView } from './TransitPreviewHeader'
import TransitPreviewSection from './TransitPreviewSection'
import TransitPreviewDetails from './TransitPreviewDetails'
import { useFetchTransitOperation } from '../../transit-operation/api/fetchTransitOperation'
import { TransitOperation } from '../../transit-operation/models/types'

export interface TransitPreviewProps {
  isVisible: boolean
  toggleVisibility: () => void
  transitOperationId: number
  originalRow: any
  openAuditModal?: () => void
  openSigningModal?: () => void
}

function TransitPreview(props: Readonly<TransitPreviewProps>) {
  const {
    isVisible,
    toggleVisibility,
    transitOperationId,
    originalRow,
    openAuditModal,
    openSigningModal,
  } = props
  const { t } = useTranslation()

  const isEns = originalRow?.declarationType === 'ENS'
  const [previewView, setPreviewView] = useState<PreviewView>('data')
  const [transitOperationData, setTransitOperationData] = useState<TransitOperation>()

  const { data: transitOperation, isLoading: isTransitOperationLoading } = useFetchTransitOperation(transitOperationId, isEns)
  const country = extractCountryFromDepartureOffice(transitOperation?.departureCustomsOffice ?? null)

  useEffect(() => {
    if (!transitOperation) return
    setTransitOperationData(transitOperation)
  }, [transitOperation])

  const changeView = (view: PreviewView) => {
    setPreviewView(view)
  }

  return (
    <Offcanvas
      className="wide transit-sidebar"
      show={isVisible}
      onHide={() => {
        toggleVisibility()
        setPreviewView('data')
      }}
      placement="end"
    >
      <TransitPreviewHeader
        transitOperationId={transitOperationId}
        transitStatus={transitOperationData?.status?.toString() ?? 'DRAFT'}
        transitOperation={transitOperationData}
        currentView={previewView}
        changeView={changeView}
        originalRow={originalRow}
        openAuditModal={openAuditModal}
        openSigningModal={openSigningModal}
      />
      {
        previewView === 'logs' && transitOperationData?.id && (
          <Offcanvas.Body key="logs-body">
            <TransitPreviewSection columnId="general-info" headingLabel={t('declaration.navbar.logs')}>
              <EventLog transitOperationId={transitOperationData.id} country={country} />
            </TransitPreviewSection>
          </Offcanvas.Body>
        )
      }
      {
        previewView === 'data' && (
          <>
            {
              isTransitOperationLoading && (
                <div>
                  <LoadingBackdrop loading={isTransitOperationLoading} isInTable={false} />
                </div>
              )
            }
            {
              !isTransitOperationLoading && (
                <TransitPreviewDetails
                  transitOperationId={transitOperationId}
                  transitOperation={transitOperationData}
                  isLoading={isTransitOperationLoading}
                />
              )
            }
          </>
        )
      }
    </Offcanvas>
  )
}

export default TransitPreview
